import { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { useMoveCard, useMoveStatus } from './api/boardHooks'
import {
	MoveCardDataProps,
	MoveStatusDataProps,
	BoardStatus,
} from '../lib/api/types/board.interfaces'
import { useQueryClient } from '@tanstack/react-query'

interface BoardData {
	data: {
		statuses: BoardStatus[]
	}
}

interface UseDragAndDropProps {
	boardId: string
	onSuccess?: () => void
}

export const useDragAndDrop = ({
	boardId,
	onSuccess,
}: UseDragAndDropProps) => {
	const { mutateAsync: moveCard } = useMoveCard()
	const { mutateAsync: moveStatus } = useMoveStatus()
	const queryClient = useQueryClient()

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event

		if (!over || active.id === over.id) return

		const isColumn = active.data.current?.type === 'column'
		const isCard = active.data.current?.type === 'card'

		if (isColumn) {
			const newIndex = over.data.current?.index

			if (typeof newIndex === 'number') {
				queryClient.setQueryData<BoardData>(
					['board', boardId],
					(oldData) => {
						if (!oldData) return oldData

						const statuses = [...oldData.data.statuses]
						const oldIndex = statuses.findIndex((s) => s.id === active.id)
						const [movedStatus] = statuses.splice(oldIndex, 1)
						statuses.splice(newIndex, 0, movedStatus)

						return {
							...oldData,
							data: {
								...oldData.data,
								statuses,
							},
						}
					}
				)

				try {
					await moveStatus({
						boardId,
						statusId: active.id as string,
						data: {
							position: newIndex,
						} as MoveStatusDataProps,
					})

					onSuccess?.()
				} catch (error) {
					console.error('Failed to move column:', error)
					// Revert the optimistic update on error
					queryClient.invalidateQueries({ queryKey: ['board', boardId] })
				}
			}
		}

		if (isCard) {
			const targetColumnId =
				over.data.current?.type === 'column'
					? over.id
					: over.data.current?.columnId
			const sourceColumnId = active.data.current?.columnId

			if (targetColumnId && sourceColumnId) {
				// Get the target column to determine the new index
				const boardData = queryClient.getQueryData<BoardData>([
					'board',
					boardId,
				])
				const targetColumn = boardData?.data?.statuses?.find(
					(s: BoardStatus) => s.id === targetColumnId
				)
				const newIndex =
					over.data.current?.type === 'column'
						? targetColumn?.cards?.length ?? 0
						: over.data.current?.index

				if (typeof newIndex === 'number') {
					// Optimistically update the UI
					queryClient.setQueryData<BoardData>(
						['board', boardId],
						(oldData) => {
							if (!oldData) return oldData

							const statuses = [...oldData.data.statuses]
							const sourceStatus = statuses.find((s) => s.id === sourceColumnId)
							const targetStatus = statuses.find((s) => s.id === targetColumnId)

							if (sourceStatus && targetStatus) {
								// Remove card from source column
								const cardIndex = sourceStatus.cards.findIndex(
									(c) => c.id === active.id
								)
								if (cardIndex === -1) return oldData
								const [movedCard] = sourceStatus.cards.splice(cardIndex, 1)

								// Update card's statusId
								movedCard.statusId = targetColumnId

								// Add card to target column at new position
								targetStatus.cards.splice(newIndex, 0, movedCard)
							}

							return {
								...oldData,
								data: {
									...oldData.data,
									statuses,
								},
							}
						}
					)

					try {
						await moveCard({
							boardId,
							cardId: active.id as string,
							data: {
								position: newIndex,
								statusId: targetColumnId,
							} as MoveCardDataProps,
						})

						onSuccess?.()
					} catch (error) {
						console.error('Failed to move card:', error)
						// Revert the optimistic update on error
						queryClient.invalidateQueries({
							queryKey: ['board', boardId],
						})
					}
				}
			}
		}
	}

	const handleDragStart = (event: DragStartEvent) => {
		// You can add any pre-drag logic here if needed
	}

	return {
		handleDragEnd,
		handleDragStart,
	}
}
